// ============================================================================
// LE TIROIR LATÉRAL — le panneau de RÉGLAGE de l'écran qu'on est en train de lire.
//
// ⚠️ POURQUOI PAS UNE MODALE : on règle ce qu'on a SOUS LES YEUX. Une modale centrée recouvre
// justement la chose qu'on ajuste, et son voile opaque empêche de vérifier l'effet du réglage. Le
// tiroir se range sur le bord, laisse la page lisible, et entre par translation depuis ce bord.
//
// ⚠️ IL PARTAGE LE CLAVIER DE LA MODALE (`overlay.ts`) et ne le réécrit pas : focus qui entre et
// revient au déclencheur, tabulation enfermée, Échap, verrou de défilement. Une seconde copie de ce
// piège aurait dérivé sans que rien ne se voie à l'écran.
// ============================================================================
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DRAWER, DRAWER_SCRIM, Z_LAYERS } from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { PlanDeSurcouche, useOverlayPanel, type OverlayPanelOptions } from './overlay';
import { t } from '@orgazone/i18n';

/** ⚠️ `open`, `onClose`, `autoFocus` et `blockClose` viennent du socle : un tiroir EST un panneau superposé. */
export interface SideDrawerProps extends OverlayPanelOptions {
  /** Titre — obligatoire : il nomme le panneau pour le lecteur d'écran autant que pour l'œil. */
  title: string;
  /** Sous-titre : à quoi le réglage s'applique (« propre à votre compte »), pas une redite du titre. */
  subtitle?: string;
  children: ReactNode;
  /**
   * Note de PIED — ce que le panneau ne fait PAS encore, ou la portée du réglage.
   * ⚠️ Pas de boutons ici : un tiroir de réglage enregistre au geste. Un « Valider » laisserait croire
   * que rien n'est pris en compte tant qu'on ne l'a pas cliqué.
   */
  footNote?: ReactNode;
}

export function SideDrawer({ open, onClose, autoFocus = 'panel', blockClose, title, subtitle, children, footNote }: SideDrawerProps) {
  const { panelRef, close } = useOverlayPanel({ open, onClose, autoFocus, blockClose });
  if (!open) return null;

  return createPortal(
    <PlanDeSurcouche.Provider value={Z_LAYERS.drawer}>
    <div
      // Le voile ferme au clic — mais seulement sur LUI, pas sur un clic relâché depuis le panneau.
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: DRAWER_SCRIM.z,
        // ⚠️ SANS FLOU, contrairement à la modale : on doit continuer à LIRE l'écran qu'on règle.
        background: `color-mix(in srgb, var(--scrim) ${DRAWER_SCRIM.alpha * 100}%, transparent)`,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          width: DRAWER.width, maxWidth: `${DRAWER.maxWidthVw}vw`, height: '100%',
          background: 'var(--paper)', boxShadow: DRAWER.shadow,
          display: 'flex', flexDirection: 'column', minHeight: 0,
          animation: `drawer-in ${DRAWER.anim.enterMs}ms ${DRAWER.anim.easing}`,
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', gap: DRAWER.head.gap,
          padding: `${DRAWER.head.padV}px ${DRAWER.head.padH}px`,
          borderBottom: '1px solid var(--ink-200)', flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: DRAWER.head.titleSize, fontWeight: 700,
              letterSpacing: DRAWER.head.titleTracking, color: 'var(--ink-900)',
            }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: DRAWER.head.subSize, color: 'var(--ink-500)' }}>{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t('Fermer le panneau')}
            style={{
              width: DRAWER.close.size, height: DRAWER.close.size, flexShrink: 0,
              border: 'none', borderRadius: `var(--r-${DRAWER.close.radius})`,
              background: `var(--${DRAWER.close.bg})`, color: `var(--${DRAWER.close.fg})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <Icon name="x" size={DRAWER.close.icon} stroke={DRAWER.close.iconStroke} />
          </button>
        </header>

        {/* ⚠️ `minHeight: 0` est STRUCTUREL : sans lui, ce corps pousse le pied hors du panneau. */}
        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          padding: `${DRAWER.body.padV}px ${DRAWER.body.padH}px`,
        }}>
          {children}
        </div>

        {footNote && (
          <footer style={{
            flexShrink: 0, padding: `${DRAWER.foot.padV}px ${DRAWER.foot.padH}px`,
            borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)',
            fontSize: DRAWER.foot.size, color: 'var(--ink-500)', lineHeight: 1.5,
          }}>
            {footNote}
          </footer>
        )}
      </aside>
    </div>
    </PlanDeSurcouche.Provider>,
    document.body,
  );
}
